import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { Icon } from "@iconify/react";
import {
  KIND_LABELS,
  VALUE_KINDS,
  appendItem,
  appendKey,
  convertValue,
  deleteAtPath,
  getValueKind,
  isPlainObject,
  renameKeyAt,
  uniqueKey,
} from "../../utils/metadata";

const fieldStyles = css`
  height: 34px;
  padding: 0 10px;
  font-size: 14px;
  font-family: inherit;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: var(--button-bg);
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EntryBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EntryRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const KeyBox = styled.input`
  ${fieldStyles}
  width: 220px;
  flex: 0 0 220px;
  font-weight: 600;
`;

const IndexLabel = styled.span`
  width: 220px;
  flex: 0 0 220px;
  font-size: 13px;
  color: #a0a6b1;
  font-style: italic;
  padding-left: 10px;
`;

const TextBox = styled.input`
  ${fieldStyles}
  flex: 1;
  min-width: 120px;
`;

const KindSelect = styled.select`
  ${fieldStyles}
  width: 130px;
  flex: 0 0 130px;
  cursor: pointer;

  /* Pure white reads better than the 80% white font colour on the dark theme's dark field.
     Scoped so the light theme (white field) keeps its dark text, which would otherwise be
     invisible. */
  [data-aw-theme="dark"] & {
    color: #ffffff;
  }

  /* Pin the open list explicitly: some browsers draw the popup with a light background, so
     inheriting white from the control above would make the options unreadable. */
  & option {
    color: var(--main-font-color);
    background-color: var(--main-bg);
  }
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  flex: 1;
  cursor: pointer;
  color: var(--main-font-color);
`;

const Muted = styled.span`
  flex: 1;
  font-size: 13px;
  font-style: italic;
  color: #a0a6b1;
`;

const IconButton = styled.div`
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  color: #a0a6b1;

  &:hover {
    background-color: var(--content-item-bg);
    color: #c0392b;
  }
`;

const AddButton = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 600;
  color: var(--button-bg);
  border: 1px dashed var(--main-border-color);
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background-color: var(--content-item-bg);
  }
`;

const NestedGroup = styled.div`
  margin-left: 18px;
  padding-left: 14px;
  border-left: 2px solid var(--content-item-border-color);
`;

// A number field keeps its own text so intermediate values like "-" or "1." don't get
// rewritten under the caret; only a parseable number is committed upward.
const NumberField = ({ value, onCommit }) => {
  const [text, setText] = useState(value === undefined || value === null ? "" : String(value));

  useEffect(() => {
    setText(value === undefined || value === null ? "" : String(value));
  }, [value]);

  return (
    <TextBox
      type="text"
      inputMode="decimal"
      value={text}
      onChange={(event) => {
        const next = event.target.value;
        setText(next);
        const parsed = Number(next);
        if (next.trim() !== "" && Number.isFinite(parsed)) {
          onCommit(parsed);
        }
      }}
    />
  );
};

// Keys are committed on blur so a rename can't collide mid-keystroke; uniqueKey stops two
// keys from merging into one.
const KeyField = ({ name, existingKeys, onRename }) => {
  const [text, setText] = useState(name);

  useEffect(() => {
    setText(name);
  }, [name]);

  const commit = () => {
    const next = uniqueKey(existingKeys.filter((key) => key !== name), text);
    if (next !== name) {
      onRename(next);
    }
    setText(next);
  };

  return (
    <KeyBox
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.currentTarget.blur();
        }
      }}
    />
  );
};

const Entry = ({
  name,
  label,
  isArrayEntry = false,
  existingKeys = [],
  value,
  onChange,
  onDelete,
  onRename,
  depth = 0,
}) => {
  const kind = getValueKind(value);
  const nested = kind === "object" || kind === "array";

  return (
    <EntryBlock>
      <EntryRow>
        {isArrayEntry ? (
          <IndexLabel>{label}</IndexLabel>
        ) : (
          <KeyField name={name} existingKeys={existingKeys} onRename={onRename} />
        )}

        <KindSelect
          value={kind}
          title="Value type"
          onChange={(event) => onChange(convertValue(event.target.value, value))}
        >
          {VALUE_KINDS.map((option) => (
            <option key={option} value={option}>{KIND_LABELS[option]}</option>
          ))}
        </KindSelect>

        {kind === "string" && (
          <TextBox
            value={value ?? ""}
            placeholder="Value"
            onChange={(event) => onChange(event.target.value)}
          />
        )}

        {kind === "number" && <NumberField value={value} onCommit={onChange} />}

        {kind === "boolean" && (
          <ToggleLabel>
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(event) => onChange(event.target.checked)}
            />
            {value ? "true" : "false"}
          </ToggleLabel>
        )}

        {kind === "null" && <Muted>null</Muted>}

        {nested && (
          <Muted>
            {Array.isArray(value) ? `List · ${value.length} item(s)` : `Object · ${Object.keys(value).length} key(s)`}
          </Muted>
        )}

        <IconButton title="Delete" onClick={onDelete}>
          <Icon icon="mdi:trash-can-outline" />
        </IconButton>
      </EntryRow>

      {nested && (
        <NestedGroup>
          <MetadataTree value={value} onChange={onChange} depth={depth + 1} />
        </NestedGroup>
      )}
    </EntryBlock>
  );
};

/**
 * Recursive editor for an arbitrary JSON value. Objects and arrays get add/delete controls;
 * leaves get a type selector so a value can be turned into text, number, boolean, null,
 * object or list.
 */
export const MetadataTree = ({ value, onChange, depth = 0 }) => {
  if (Array.isArray(value)) {
    return (
      <Container>
        {value.map((item, index) => (
          <Entry
            key={`index-${index}`}
            isArrayEntry
            label={`[${index}]`}
            value={item}
            depth={depth}
            onChange={(next) => onChange(value.map((current, i) => (i === index ? next : current)))}
            onDelete={() => onChange(deleteAtPath(value, [index]))}
          />
        ))}
        <AddButton onClick={() => onChange(appendItem(value, [], ""))}>
          <Icon icon="mdi:plus" /> Add item
        </AddButton>
      </Container>
    );
  }

  if (isPlainObject(value)) {
    const keys = Object.keys(value);

    return (
      <Container>
        {keys.map((key) => (
          <Entry
            key={`key-${key}`}
            name={key}
            existingKeys={keys}
            value={value[key]}
            depth={depth}
            onChange={(next) => onChange({ ...value, [key]: next })}
            onDelete={() => onChange(deleteAtPath(value, [key]))}
            onRename={(nextKey) => onChange(renameKeyAt(value, [key], nextKey))}
          />
        ))}
        <AddButton onClick={() => onChange(appendKey(value, [], ""))}>
          <Icon icon="mdi:plus" /> Add key
        </AddButton>
      </Container>
    );
  }

  return <Muted>This value is not an object or list.</Muted>;
};

export default MetadataTree;
